import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2, Clock, XCircle, Sparkles } from "lucide-react";
import { SrLogo } from "../../components/smartreach/Chrome";
import { api } from "../../lib/api";
import { login, sr } from "../../lib/sr";

export default function SignIn({ mode = "signin" }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isSignup = mode === "signup";
  const budgetParam = Number(params.get("budget")) || "";
  const [form, setForm] = useState({ name: "", email: "", password: "", business: "", city: "", phone: "", sms_consent: false, intended_monthly_budget: budgetParam });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [submitted, setSubmitted] = useState(false);
  const [notice, setNotice] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const brandHost = params.get("brand_host") || (typeof window !== "undefined" ? window.location.hostname : "");
  const [brand, setBrand] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("wl_brand") || "null"); } catch { return null; }
  });
  const accent = brand?.accent_color;

  useEffect(() => {
    if (!brandHost) return;
    sr.resolveDomain(brandHost).then((r) => { if (r?.found && r.branding) setBrand(r.branding); }).catch(() => {});
  }, [brandHost]);

  useEffect(() => {
    if (!isSignup) return;
    const biz = (form.business || "").trim();
    if (biz.length < 3) { setSuggestion(null); return; }
    const timer = setTimeout(() => {
      sr.budgetSuggestion({ business: biz, city: form.city })
        .then((s) => setSuggestion(s)).catch(() => setSuggestion(null));
    }, 450);
    return () => clearTimeout(timer);
  }, [form.business, form.city, isSignup]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      if (isSignup) {
        const body = { ...form };
        body.intended_monthly_budget = Number(form.intended_monthly_budget) > 0 ? Number(form.intended_monthly_budget) : null;
        await api.post("/public/smartreach/signup", body);
        setSubmitted(true);
      } else {
        await login(form.email, form.password);
        toast.success("Welcome back");
        nav("/app");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong";
      const status = err?.response?.status;
      if (status === 402 || /choose a plan/i.test(detail)) { toast.error(detail); nav("/pricing"); }
      else if (status === 403 && /await|approv|declined/i.test(detail)) {
        setNotice({ kind: /declined/i.test(detail) ? "rejected" : "pending", detail });
      } else { toast.error(detail); }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sr-root" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", minHeight: "100vh" }}>
      <div className="sr-hero" data-testid="auth-hero" style={{ display: "flex", alignItems: "center", padding: 56, ...(accent ? { background: accent } : {}) }}>
        <div>
          {brand ? (
            brand.logo_url
              ? <img src={brand.logo_url} alt={brand.name} data-testid="brand-logo" style={{ maxHeight: 48, maxWidth: 220, objectFit: "contain" }} />
              : <h2 data-testid="brand-name" style={{ color: "#fff", fontSize: 28, margin: 0 }}>{brand.name}</h2>
          ) : <SrLogo light />}
          <h1 style={{ color: "#fff", fontSize: 40, marginTop: 28 }}>{brand ? `Welcome to ${brand.name}.` : "Turn local attention into customers."}</h1>
          <p style={{ marginTop: 16, maxWidth: 420 }}>
            {brand ? "Sign in to your marketing dashboard." : "Tell SmartReachAI your goal and budget. Get a complete local growth plan in under ten minutes."}
          </p>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
        {submitted ? (
          <div className="sr-card sr-fade" style={{ width: "100%", maxWidth: 420, textAlign: "center" }} data-testid="signup-submitted">
            <div style={{ width: 60, height: 60, borderRadius: 999, background: "#E6F4EA", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Clock size={30} color="#067647" /></div>
            <h2 style={{ fontSize: 24, marginTop: 14 }}>Account request submitted</h2>
            <p className="sr-muted" style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.5 }}>
              Thanks{form.name ? `, ${form.name}` : ""}! Your account request has been submitted. <strong style={{ color: "var(--navy)" }}>You can start your campaign once it's approved.</strong> We'll email <strong style={{ color: "var(--navy)" }}>{form.email}</strong> as soon as your workspace is activated.
            </p>
            <a href="/login" className="sr-btn sr-btn-ghost" style={{ marginTop: 20, justifyContent: "center", width: "100%" }} data-testid="submitted-to-login">Back to sign in</a>
          </div>
        ) : (
        <form onSubmit={submit} className="sr-card sr-fade" style={{ width: "100%", maxWidth: 400 }} data-testid="auth-form">
          <h2 style={{ fontSize: 26 }}>{isSignup ? "Create your account" : "Welcome back"}</h2>
          <p className="sr-muted" style={{ marginTop: 6, marginBottom: 22, fontSize: 14 }}>
            {isSignup ? "Request your account — we'll review and approve it, then you can start." : "Log in to your growth workspace."}
          </p>
          {notice && (
            <div data-testid="login-notice" style={{
              display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 14px", borderRadius: 10, marginBottom: 18,
              background: notice.kind === "rejected" ? "#FEF3F2" : "#FFFAEB",
              border: `1px solid ${notice.kind === "rejected" ? "#FDA29B" : "#FEC84B"}`,
            }}>
              {notice.kind === "rejected" ? <XCircle size={18} color="#B42318" style={{ flexShrink: 0, marginTop: 1 }} /> : <Clock size={18} color="#B54708" style={{ flexShrink: 0, marginTop: 1 }} />}
              <div style={{ fontSize: 13.5, color: notice.kind === "rejected" ? "#B42318" : "#B54708" }}>
                <strong>{notice.kind === "rejected" ? "Account request declined" : "Awaiting approval"}</strong>
                <div style={{ marginTop: 2 }}>{notice.detail}</div>
              </div>
            </div>
          )}
          {isSignup && (
            <>
              <div className="sr-field"><label className="sr-label">Your name</label>
                <input className="sr-input" value={form.name} onChange={set("name")} required data-testid="auth-name" /></div>
              <div className="sr-field"><label className="sr-label">Business name</label>
                <input className="sr-input" value={form.business} onChange={set("business")} placeholder="Optional" data-testid="auth-business" /></div>
              <div className="sr-field"><label className="sr-label">City <span className="sr-muted" style={{ fontWeight: 400 }}>(optional)</span></label>
                <input className="sr-input" value={form.city} onChange={set("city")} placeholder="e.g. Dallas" data-testid="auth-city" /></div>
              <div className="sr-field"><label className="sr-label">Monthly marketing budget</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 700, color: "var(--muted)" }}>$</span>
                  <input className="sr-input" type="number" min="0" step="100" value={form.intended_monthly_budget}
                    onChange={set("intended_monthly_budget")} placeholder="e.g. 3000" data-testid="auth-budget" style={{ flex: 1 }} /><span className="sr-muted" style={{ fontSize: 13 }}>/mo</span>
                </div>
                {suggestion && (
                  <button type="button" onClick={() => setForm((f) => ({ ...f, intended_monthly_budget: suggestion.suggested_budget }))}
                    data-testid="budget-suggestion" style={{
                      display: "flex", gap: 8, alignItems: "flex-start", width: "100%", textAlign: "left",
                      marginTop: 8, padding: "9px 12px", borderRadius: 10, cursor: "pointer",
                      background: "#EEF4FF", border: "1px solid #C7D7FE", color: "var(--navy)",
                    }}>
                    <Sparkles size={15} color="var(--blue)" style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 12.5, lineHeight: 1.5, flex: 1 }}>
                      <span>Suggested for a {suggestion.label}: <strong>${suggestion.suggested_budget.toLocaleString()}/mo</strong> — tap to use</span>
                      <span style={{ display: "block", marginTop: 2 }} className="sr-muted" data-testid="budget-range">{suggestion.range_label}</span>
                      <span style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3 }} data-testid="budget-confidence">
                        <span style={{ width: 6, height: 6, borderRadius: 999, flexShrink: 0, background: suggestion.confidence === "high" ? "#12B76A" : suggestion.confidence === "medium" ? "#F79009" : "#98A2B3" }} />
                        <span className="sr-muted" style={{ fontSize: 11 }}>{suggestion.confidence_note}</span>
                      </span>
                    </span>
                  </button>
                )}
                <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>Set your own spend like Google or Meta — we'll build your growth plan around it. You can change it anytime.</p>
              </div>
              <div className="sr-field"><label className="sr-label">Mobile phone <span className="sr-muted" style={{ fontWeight: 400 }}>(optional)</span></label>
                <input className="sr-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" data-testid="auth-phone" /></div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 9, margin: "2px 0 18px", cursor: "pointer" }}>
                <input type="checkbox" checked={form.sms_consent} onChange={(e) => setForm({ ...form, sms_consent: e.target.checked })} data-testid="auth-sms-consent" style={{ marginTop: 3, flexShrink: 0 }} />
                <span className="sr-muted" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
                  Text me at this number. I agree to receive recurring automated marketing, appointment/booking, and account/service text messages from SmartReach AI. Consent is not a condition of purchase. Msg frequency varies (up to 6 msgs/month). Msg &amp; data rates may apply. Reply HELP for help, STOP to cancel. See our <a href="/terms" target="_blank" rel="noreferrer">Terms</a> &amp; <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                </span>
              </label>
            </>
          )}
          <div className="sr-field"><label className="sr-label">Email</label>
            <input className="sr-input" type="email" value={form.email} onChange={set("email")} required data-testid="auth-email" /></div>
          <div className="sr-field"><label className="sr-label">Password</label>
            <input className="sr-input" type="password" value={form.password} onChange={set("password")} required data-testid="auth-password" /></div>
          <button className="sr-btn sr-btn-primary" style={{ width: "100%", justifyContent: "center", ...(accent ? { background: accent, borderColor: accent } : {}) }} disabled={busy} data-testid="auth-submit">
            {busy ? <Loader2 size={16} className="sr-spin" /> : <>{isSignup ? "Create account" : "Log in"} <ArrowRight size={16} /></>}
          </button>
          <p className="sr-muted" style={{ marginTop: 18, fontSize: 14, textAlign: "center" }}>
            {isSignup ? (
              <>Already have an account? <a href="/login" data-testid="switch-signin">Log in</a></>
            ) : (
              <>New here? <a href="/signup" data-testid="switch-signup">Create an account</a></>
            )}
          </p>
          {!isSignup && (
            <p className="sr-muted" style={{ marginTop: 6, fontSize: 13, textAlign: "center" }}>
              Counter staff? <a href="/counter" data-testid="counter-staff-link">Enter with a PIN</a>
            </p>
          )}
        </form>
        )}
      </div>
    </div>
  );
}
